/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { MerchantStoreInfo } from "types/models/menu"
import { FetchCityListResponse } from "order-manager/types/api/orderFetchCityList.type"
import { StoreMigrationMissingInfoSummary } from "types/api"
import {
  merchantGroupEndpoints,
  dpApiEndpoints,
  MERCHANT_GROUP_CACHE_TAGS,
} from "./constants"
import { FetchCityListRequest } from "./types"
import { dpApiBaseApi } from "./baseApis"

// Request/Response types

export interface SearchMerchantGroupStoresParams {
  offset?: number
  limit?: number
  search?: string
  includeInactive?: boolean
  cityIDs?: string[]
  asc?: boolean
  orderBy?: string
  includeItemsWithoutPhotosCount?: boolean
  modelType?: string
  includeMenuGroupV2ID?: boolean
}

export interface SearchMerchantGroupStoresResponse {
  merchants: MerchantStoreInfo[]
  merchantGroupID: string
  migrationMissingInfo?: StoreMigrationMissingInfoSummary
  /** Omitted from the response when there are no further pages. */
  hasMore?: boolean
}

// API Slice

export const merchantGroupApi = dpApiBaseApi.injectEndpoints({
  endpoints: builder => ({
    searchMerchantGroupStores: builder.query<
      SearchMerchantGroupStoresResponse,
      SearchMerchantGroupStoresParams
    >({
      query: ({
        offset = 0,
        limit = 10,
        search = "",
        includeInactive = true,
        cityIDs = ["ALL"],
        asc = true,
        orderBy,
        includeItemsWithoutPhotosCount = true,
        modelType = "integrated",
        includeMenuGroupV2ID = false,
      }) => ({
        url: merchantGroupEndpoints.searchStores,
        params: {
          offset,
          limit,
          search,
          includeInactive,
          cityIDs,
          asc,
          ...(orderBy !== undefined && { orderBy }),
          includeItemsWithoutPhotosCount,
          modelType,
          includeMenuGroupV2ID,
        },
      }),
      providesTags: [MERCHANT_GROUP_CACHE_TAGS.stores],
    }),
    fetchCityList: builder.query<FetchCityListResponse, FetchCityListRequest>({
      query: ({ includeInactive = true }) => ({
        url: dpApiEndpoints.fetchCityList,
        params: { includeInactive },
      }),
    }),
  }),
})

export const {
  useSearchMerchantGroupStoresQuery,
  useLazySearchMerchantGroupStoresQuery,
  useFetchCityListQuery,
} = merchantGroupApi
